import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Mail,
  Eye,
  RotateCcw,
  Save,
  Palette,
  Image,
  CheckCircle2,
  Plus,
  Copy,
  Trash2,
  Power,
  Loader2,
  Zap,
  Users,
  Shield,
  Bell,
} from "lucide-react";

// Workflow metadata — defines the automation triggers
const WORKFLOWS = {
  welcome_lead: {
    label: "Player Welcome",
    color: "bg-blue-100 text-blue-800 border-blue-200",
    icon: Users,
    description: "Sent to players when they submit the registration form",
  },
  welcome_coach: {
    label: "Coach Welcome",
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    icon: Shield,
    description: "Sent to coaches when they submit the inquiry form",
  },
  admin_new_lead: {
    label: "Admin: New Player",
    color: "bg-amber-100 text-amber-800 border-amber-200",
    icon: Bell,
    description: "Sent to admins when a new player lead is submitted",
  },
  admin_new_coach: {
    label: "Admin: New Coach",
    color: "bg-purple-100 text-purple-800 border-purple-200",
    icon: Bell,
    description: "Sent to admins when a new coach inquiry is submitted",
  },
  follow_up_reminder: {
    label: "Follow-up Reminder",
    color: "bg-rose-100 text-rose-800 border-rose-200",
    icon: Zap,
    description: "Sent to admins when a follow-up is due",
  },
};

// Editable fields per workflow type
const EDITABLE_FIELDS_BY_WORKFLOW = {
  welcome_lead: [
    { key: "club_name", label: "Club Name", type: "text" },
    { key: "tagline", label: "Tagline", type: "text" },
    { key: "logo_url", label: "Logo URL", type: "url", icon: Image },
    { key: "welcome_message", label: "Welcome Message", type: "textarea" },
    { key: "next_steps", label: "Next Steps Message", type: "textarea" },
    { key: "closing_message", label: "Closing Message", type: "textarea" },
    { key: "cta_button_text", label: "Button Text", type: "text" },
    { key: "cta_button_url", label: "Button URL", type: "url" },
    { key: "location", label: "Location", type: "text" },
    { key: "contact_email", label: "Contact Email", type: "email" },
    { key: "contact_phone", label: "Contact Phone", type: "tel" },
    { key: "header_color", label: "Header Color", type: "color", icon: Palette },
    { key: "primary_color", label: "Primary Color", type: "color", icon: Palette },
    { key: "accent_color", label: "Accent Color", type: "color", icon: Palette },
  ],
  welcome_coach: [
    { key: "club_name", label: "Club Name", type: "text" },
    { key: "tagline", label: "Tagline", type: "text" },
    { key: "logo_url", label: "Logo URL", type: "url", icon: Image },
    { key: "welcome_message", label: "Welcome Message", type: "textarea" },
    { key: "next_steps", label: "Next Steps Message", type: "textarea" },
    { key: "closing_message", label: "Closing Message", type: "textarea" },
    { key: "cta_button_text", label: "Button Text", type: "text" },
    { key: "cta_button_url", label: "Button URL", type: "url" },
    { key: "location", label: "Location", type: "text" },
    { key: "contact_email", label: "Contact Email", type: "email" },
    { key: "contact_phone", label: "Contact Phone", type: "tel" },
    { key: "header_color", label: "Header Color", type: "color", icon: Palette },
    { key: "primary_color", label: "Primary Color", type: "color", icon: Palette },
    { key: "accent_color", label: "Accent Color", type: "color", icon: Palette },
  ],
  admin_new_lead: [
    { key: "club_name", label: "Club Name", type: "text" },
    { key: "header_color", label: "Header Color", type: "color", icon: Palette },
    { key: "primary_color", label: "Primary Color", type: "color", icon: Palette },
    { key: "accent_color", label: "Accent Color", type: "color", icon: Palette },
  ],
  admin_new_coach: [
    { key: "club_name", label: "Club Name", type: "text" },
    { key: "header_color", label: "Header Color", type: "color", icon: Palette },
    { key: "primary_color", label: "Primary Color", type: "color", icon: Palette },
    { key: "accent_color", label: "Accent Color", type: "color", icon: Palette },
  ],
  follow_up_reminder: [
    { key: "primary_color", label: "Primary Color", type: "color", icon: Palette },
    { key: "accent_color", label: "Accent Color", type: "color", icon: Palette },
  ],
};

const WorkflowBadge = ({ workflowId }) => {
  const wf = WORKFLOWS[workflowId];
  if (!wf) return null;
  const Icon = wf.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded border ${wf.color}`}>
      <Icon size={10} />
      {wf.label}
    </span>
  );
};

const EmailTemplates = () => {
  const [templates, setTemplates] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState("welcome_lead");
  const [editValues, setEditValues] = useState({});
  const [editSubject, setEditSubject] = useState("");
  const [editName, setEditName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newWorkflow, setNewWorkflow] = useState("welcome_lead");
  const [newSource, setNewSource] = useState("");
  const [filterWorkflow, setFilterWorkflow] = useState("all");
  const [availableSources, setAvailableSources] = useState([]);

  const fetchTemplates = useCallback(async () => {
    try {
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API}/templates`, { withCredentials: true }),
        axios.get(`${API}/sources`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setTemplates(tRes.data);
      setAvailableSources(sRes.data || []);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (templates[selectedTemplate]) {
      setEditValues(templates[selectedTemplate].values || {});
      setEditSubject(templates[selectedTemplate].subject || "");
      setEditName(templates[selectedTemplate].name || "");
    }
  }, [selectedTemplate, templates]);

  const saveTemplate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/templates/${selectedTemplate}`, {
        values: editValues,
        subject: editSubject,
        name: editName,
      }, { withCredentials: true });
      toast.success("Template saved");
      fetchTemplates();
    } catch {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const resetTemplate = async () => {
    try {
      await axios.post(`${API}/templates/${selectedTemplate}/reset`, {}, { withCredentials: true });
      toast.success("Template reset to default");
      fetchTemplates();
    } catch {
      toast.error("Failed to reset template");
    }
  };

  const previewTemplate = async () => {
    try {
      const t = templates[selectedTemplate];
      const workflowId = t?.workflow || selectedTemplate;
      const previewId = workflowId in WORKFLOWS ? workflowId : selectedTemplate;
      const response = await axios.post(`${API}/templates/${previewId}/preview`, {
        values: editValues,
        subject: editSubject,
      }, { withCredentials: true });
      setPreviewHtml(response.data.html);
      setPreviewSubject(response.data.subject);
      setShowPreview(true);
    } catch {
      toast.error("Failed to generate preview");
    }
  };

  const activateTemplate = async (templateId) => {
    try {
      await axios.put(`${API}/templates/${templateId}/activate`, {}, { withCredentials: true });
      toast.success("Template activated");
      fetchTemplates();
    } catch {
      toast.error("Failed to activate template");
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/templates/${templateId}`, { withCredentials: true });
      toast.success("Template deleted");
      if (selectedTemplate === templateId) {
        const t = templates[templateId];
        setSelectedTemplate(t?.workflow || "welcome_lead");
      }
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const createVariant = async () => {
    if (!newName.trim()) return;
    try {
      const res = await axios.post(`${API}/templates/create`, {
        workflow: newWorkflow,
        name: newName.trim(),
        source: newSource || "",
      }, { withCredentials: true });
      toast.success("Template created");
      setShowCreate(false);
      setNewName("");
      setNewSource("");
      await fetchTemplates();
      setSelectedTemplate(res.data.template_id);
    } catch {
      toast.error("Failed to create template");
    }
  };

  const handleValueChange = (key, value) => {
    setEditValues({ ...editValues, [key]: value });
  };

  const currentTemplate = templates[selectedTemplate];
  const currentWorkflow = currentTemplate?.workflow || selectedTemplate;
  const editableFields = EDITABLE_FIELDS_BY_WORKFLOW[currentWorkflow] || [];

  // Group and filter templates
  const templateEntries = Object.entries(templates);
  const filteredTemplates = filterWorkflow === "all"
    ? templateEntries
    : templateEntries.filter(([, t]) => t.workflow === filterWorkflow);

  // Group by workflow for sidebar display
  const groupedByWorkflow = {};
  filteredTemplates.forEach(([id, t]) => {
    const wf = t.workflow || id;
    if (!groupedByWorkflow[wf]) groupedByWorkflow[wf] = [];
    groupedByWorkflow[wf].push({ id, ...t });
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-cfc-primary" />
      </div>
    );
  }

  return (
    <div data-testid="email-templates">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-cfc-primary">Email Templates</h1>
          <p className="text-cfc-text-muted text-sm mt-1">Customize email notifications for each workflow. Create variants and set which is active.</p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="bg-cfc-primary hover:bg-cfc-primary-hover"
          data-testid="create-template-btn"
        >
          <Plus size={16} className="mr-2" /> New Template
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Template List Sidebar */}
        <div className="lg:col-span-1 space-y-3">
          {/* Workflow Filter */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            <button
              onClick={() => setFilterWorkflow("all")}
              className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${filterWorkflow === "all" ? "bg-cfc-primary text-white border-cfc-primary" : "bg-white text-cfc-text-muted border-cfc-border hover:bg-cfc-cream"}`}
              data-testid="filter-all"
            >
              All
            </button>
            {Object.entries(WORKFLOWS).map(([wfId, wf]) => (
              <button
                key={wfId}
                onClick={() => setFilterWorkflow(wfId)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${filterWorkflow === wfId ? "bg-cfc-primary text-white border-cfc-primary" : "bg-white text-cfc-text-muted border-cfc-border hover:bg-cfc-cream"}`}
                data-testid={`filter-${wfId}`}
              >
                {wf.label}
              </button>
            ))}
          </div>

          {/* Grouped template cards */}
          {Object.entries(groupedByWorkflow).map(([wfId, items]) => (
            <div key={wfId} className="space-y-1.5">
              <div className="flex items-center gap-2 px-1 pt-2">
                <WorkflowBadge workflowId={wfId} />
                <span className="text-[10px] text-cfc-text-muted">{items.length} template{items.length > 1 ? "s" : ""}</span>
              </div>
              {items.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSelectedTemplate(t.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    selectedTemplate === t.id
                      ? "bg-cfc-primary text-white border-cfc-primary shadow-sm"
                      : "bg-white hover:bg-cfc-cream border-cfc-border"
                  }`}
                  data-testid={`template-btn-${t.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{t.name}</p>
                      <p className={`text-[11px] mt-0.5 truncate ${selectedTemplate === t.id ? "text-white/70" : "text-cfc-text-muted"}`}>
                        {t.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {t.source && (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-blue-100 text-blue-800">
                          {t.source.replace(/_/g, " ").toUpperCase()}
                        </span>
                      )}
                      {t.is_active && (
                        <span className={`flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold rounded ${selectedTemplate === t.id ? "bg-white/20 text-white" : "bg-green-100 text-green-800"}`}>
                          <Power size={8} /> ACTIVE{t.source ? "" : " (DEFAULT)"}
                        </span>
                      )}
                      {t.is_customized && !t.is_active && (
                        <CheckCircle2 size={14} className={selectedTemplate === t.id ? "text-white/70" : "text-green-600"} />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Template Editor */}
        <div className="lg:col-span-2">
          {currentTemplate && (
            <Card className="border-cfc-border">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <WorkflowBadge workflowId={currentWorkflow} />
                      {currentTemplate.is_active && (
                        <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50 text-[10px]">
                          <Power size={8} className="mr-1" /> Active
                        </Badge>
                      )}
                      {currentTemplate.is_default && (
                        <Badge variant="outline" className="text-cfc-text-muted border-cfc-border text-[10px]">Default</Badge>
                      )}
                    </div>
                    <CardTitle className="font-heading text-lg text-cfc-primary flex items-center gap-2">
                      <Mail size={18} />
                      {currentTemplate.name}
                    </CardTitle>
                    <p className="text-xs text-cfc-text-muted mt-1">
                      {WORKFLOWS[currentWorkflow]?.description || currentTemplate.description}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1.5 shrink-0">
                    {!currentTemplate.is_active && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => activateTemplate(selectedTemplate)}
                        className="text-green-700 border-green-300 hover:bg-green-50"
                        data-testid="activate-template-btn"
                      >
                        <Power size={14} className="mr-1" /> Activate
                      </Button>
                    )}
                    {!currentTemplate.is_default && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteTemplate(selectedTemplate)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid="delete-template-btn"
                      >
                        <Trash2 size={14} className="mr-1" /> Delete
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={resetTemplate} data-testid="reset-template-btn">
                      <RotateCcw size={14} className="mr-1" /> Reset
                    </Button>
                    <Button variant="outline" size="sm" onClick={previewTemplate} data-testid="preview-template-btn">
                      <Eye size={14} className="mr-1" /> Preview
                    </Button>
                    <Button
                      size="sm"
                      onClick={saveTemplate}
                      disabled={saving}
                      className="bg-cfc-accent hover:bg-cfc-accent-hover text-cfc-primary"
                      data-testid="save-template-btn"
                    >
                      <Save size={14} className="mr-1" /> {saving ? "..." : "Save"}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="content" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="content">Content</TabsTrigger>
                    <TabsTrigger value="branding">Branding</TabsTrigger>
                  </TabsList>

                  <TabsContent value="content" className="space-y-4 mt-4">
                    {/* Template Name (for custom variants) */}
                    {!currentTemplate.is_default && (
                      <div>
                        <Label className="text-cfc-primary font-medium">Template Name</Label>
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="mt-1"
                          placeholder="e.g., Fall 2025 Coach Welcome"
                          data-testid="template-name-input"
                        />
                      </div>
                    )}

                    {/* Subject Line */}
                    <div>
                      <Label className="text-cfc-primary font-medium">Email Subject</Label>
                      <Input
                        value={editSubject}
                        onChange={(e) => setEditSubject(e.target.value)}
                        className="mt-1"
                        placeholder="Email subject line..."
                        data-testid="template-subject-input"
                      />
                      <p className="text-xs text-cfc-text-muted mt-1">
                        Use {"{name}"}, {"{player_age}"}, {"{organization}"} for dynamic values
                      </p>
                    </div>

                    {/* Content Fields */}
                    {editableFields
                      .filter(f => f.type !== "color")
                      .map((field) => (
                        <div key={field.key}>
                          <Label className="text-cfc-primary font-medium flex items-center gap-2">
                            {field.icon && <field.icon size={14} />}
                            {field.label}
                          </Label>
                          {field.type === "textarea" ? (
                            <Textarea
                              value={editValues[field.key] || ""}
                              onChange={(e) => handleValueChange(field.key, e.target.value)}
                              className="mt-1"
                              rows={3}
                              data-testid={`template-field-${field.key}`}
                            />
                          ) : (
                            <Input
                              type={field.type === "url" ? "text" : field.type}
                              value={editValues[field.key] || ""}
                              onChange={(e) => handleValueChange(field.key, e.target.value)}
                              className="mt-1"
                              data-testid={`template-field-${field.key}`}
                            />
                          )}
                        </div>
                      ))}
                  </TabsContent>

                  <TabsContent value="branding" className="space-y-4 mt-4">
                    <div className="grid grid-cols-3 gap-4">
                      {editableFields
                        .filter(f => f.type === "color")
                        .map((field) => (
                          <div key={field.key}>
                            <Label className="text-cfc-primary font-medium flex items-center gap-2">
                              <Palette size={14} />
                              {field.label}
                            </Label>
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="color"
                                value={editValues[field.key] || "#1B3D2F"}
                                onChange={(e) => handleValueChange(field.key, e.target.value)}
                                className="w-12 h-10 rounded border border-cfc-border cursor-pointer"
                                data-testid={`template-color-${field.key}`}
                              />
                              <Input
                                value={editValues[field.key] || ""}
                                onChange={(e) => handleValueChange(field.key, e.target.value)}
                                className="flex-1"
                                placeholder="#1B3D2F"
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                    <div className="p-4 bg-cfc-cream rounded mt-4">
                      <p className="text-sm text-cfc-text-muted">
                        <strong>Tip:</strong> Use your club{"'"}s brand colors. Header = email header background,
                        Primary = headings, Accent = buttons and highlights.
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>

                {/* Available Variables */}
                <div className="mt-6 p-4 bg-cfc-cream-alt rounded">
                  <p className="text-sm font-medium text-cfc-primary mb-2">Available Variables</p>
                  <div className="flex flex-wrap gap-2">
                    {currentTemplate.variables?.map((v) => (
                      <code key={v} className="text-xs bg-white px-2 py-1 rounded border border-cfc-border">
                        {`{${v}}`}
                      </code>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-cfc-primary">Email Preview</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="mb-4 p-3 bg-cfc-cream rounded">
              <p className="text-sm text-cfc-text-muted">Subject:</p>
              <p className="font-medium text-cfc-primary">{previewSubject}</p>
            </div>
            <div
              className="border border-cfc-border rounded overflow-hidden"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Template Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md" data-testid="create-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-cfc-primary">Create New Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label>Template Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g., Fall 2025 Coach Welcome"
                data-testid="new-template-name"
              />
            </div>
            <div>
              <Label>Workflow</Label>
              <p className="text-xs text-cfc-text-muted mb-2">Which automation trigger should use this template?</p>
              <div className="space-y-2">
                {Object.entries(WORKFLOWS).map(([wfId, wf]) => {
                  const Icon = wf.icon;
                  return (
                    <button
                      key={wfId}
                      onClick={() => setNewWorkflow(wfId)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        newWorkflow === wfId
                          ? "border-cfc-primary bg-cfc-primary/5"
                          : "border-cfc-border hover:bg-cfc-cream"
                      }`}
                      data-testid={`workflow-option-${wfId}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon size={16} className={newWorkflow === wfId ? "text-cfc-primary" : "text-cfc-text-muted"} />
                        <div>
                          <p className="text-sm font-medium text-cfc-primary">{wf.label}</p>
                          <p className="text-[11px] text-cfc-text-muted">{wf.description}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            {availableSources.length > 0 && (
              <div>
                <Label>Source (optional)</Label>
                <p className="text-xs text-cfc-text-muted mb-2">Assign this template to a specific lead source. When a lead from this source arrives, this template will be used.</p>
                <div className="space-y-2">
                  <button
                    onClick={() => setNewSource("")}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${!newSource ? "border-cfc-primary bg-cfc-primary/5" : "border-cfc-border hover:bg-cfc-cream"}`}
                    data-testid="source-option-any"
                  >
                    <p className="text-sm font-medium text-cfc-primary">Any Source (default)</p>
                    <p className="text-[11px] text-cfc-text-muted">Used when no source-specific template exists</p>
                  </button>
                  {availableSources.map((s) => (
                    <button
                      key={s.source}
                      onClick={() => setNewSource(s.source)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${newSource === s.source ? "border-cfc-primary bg-cfc-primary/5" : "border-cfc-border hover:bg-cfc-cream"}`}
                      data-testid={`source-option-${s.source}`}
                    >
                      <p className="text-sm font-medium text-cfc-primary">{s.label}</p>
                      <code className="text-[10px] text-cfc-text-muted">{s.source}</code>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button
              onClick={createVariant}
              disabled={!newName.trim()}
              className="bg-cfc-primary hover:bg-cfc-primary-hover"
              data-testid="confirm-create-template"
            >
              Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplates;
